// Пароль на экране: что показать про срок и что не так с новым паролем.
//
// Правила — те же, что на бэкенде (cdp/auth/password_policy.py), но здесь они
// только подсказка человеку до отправки: решение всё равно принимает сервер, и
// требования приходят от него (`requirements`, `min_length`).

/** Состояние пароля, как его отдаёт GET /auth/password/state. */
export interface PasswordState {
  /** Есть ли у этого человека пароль вообще (вход по ЭЦП/ЕСИ — нет). */
  applies: boolean;
  expired: boolean;
  must_change: boolean;
  days_left: number | null;
  warn: boolean;
  min_length: number;
  max_age_days: number;
  warning_days: number;
  history_count: number;
  requirements: string[];
}

export interface ExpiryNotice {
  text: string;
  color: string;
}

/** Склонение суток: «через 1 сутки» читается плохо, «через 1 день» — нормально. */
function days(count: number): string {
  const abs = Math.abs(count) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return `${count} дней`;
  if (last === 1) return `${count} день`;
  if (last >= 2 && last <= 4) return `${count} дня`;
  return `${count} дней`;
}

/**
 * Что сказать человеку про срок пароля, или null, если говорить нечего.
 *
 * Просрочка — красная: работать всё равно нельзя. Предупреждение — жёлтое:
 * работать можно, но пора менять.
 */
export function expiryNotice(state: PasswordState | null): ExpiryNotice | null {
  if (!state || !state.applies) return null;

  if (state.must_change) {
    return {
      text: 'Администратор назначил смену пароля. Смените пароль, чтобы продолжить работу.',
      color: 'red',
    };
  }
  if (state.expired) {
    return {
      text: 'Срок действия пароля истёк. Смените пароль, чтобы продолжить работу.',
      color: 'red',
    };
  }
  if (state.warn && state.days_left !== null) {
    const left = state.days_left <= 0 ? 'сегодня' : `через ${days(state.days_left)}`;
    return { text: `Срок действия пароля истекает ${left}. Пора сменить пароль.`, color: 'yellow' };
  }
  return null;
}

/**
 * Что не так с новым паролем — для подсказки до отправки.
 *
 * Историю паролей здесь проверить нельзя (хеши на сервере), поэтому её нет в
 * списке: про повтор скажет сервер.
 */
export function newPasswordIssues(
  password: string,
  confirm: string,
  // Минимальная длина — всё, что нужно от политики. Так подсказки работают и на
  // публичной странице смены забытого пароля, где состояния пароля нет.
  state: { min_length?: number } | null,
  email?: string | null,
): string[] {
  const minLength = state?.min_length ?? 10;
  const issues: string[] = [];

  if (password.length < minLength) issues.push(`не короче ${minLength} символов`);
  if (!/[a-zа-яё]/.test(password)) issues.push('нужна строчная буква');
  if (!/[A-ZА-ЯЁ]/.test(password)) issues.push('нужна заглавная буква');
  if (!/\d/.test(password)) issues.push('нужна цифра');

  if (email) {
    const candidate = password.trim().toLowerCase();
    const address = email.trim().toLowerCase();
    if (candidate === address || candidate === address.split('@')[0]) {
      issues.push('пароль не должен совпадать с логином и почтой');
    }
  }
  if (password && confirm && password !== confirm) {
    issues.push('пароль и подтверждение не совпадают');
  }
  return issues;
}
